/* eslint-disable no-console */
import * as Sentry from "@sentry/react"
import { isArray, isString } from "lodash"
import { GraphQLFormattedError } from "graphql"

import { track } from "./analytics"

const isProd = () => process.env.REACT_APP_ENV === "production"
type Extras = Record<string, any>

type ErrorWithMessage = {
  message: string
}

type ErrorWithName = ErrorWithMessage & {
  name: string
}

type GraphQLErrorContainer = {
  graphQLErrors: readonly GraphQLFormattedError[]
}

type GraphQLErrorExtensions = {
  code?: string
  reportToSentry?: boolean
}

type SentryScope = {
  setExtras: (extras: Record<string, unknown>) => void
  setFingerprint: (fingerprint: string[]) => void
  setTag: (key: string, value: string | boolean) => void
}

const shouldReportToSentry = (extras?: Extras): boolean => {
  return isProd() && extras?.reportToSentry !== false
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as Record<string, unknown>).message === "string"
  )
}

function isErrorWithName(error: unknown): error is ErrorWithName {
  return isErrorWithMessage(error) && "name" in error && isString(error.name)
}

function hasGraphQLErrors(error: unknown): error is GraphQLErrorContainer {
  return (
    typeof error === "object" &&
    error !== null &&
    "graphQLErrors" in error &&
    isArray((error as { graphQLErrors?: unknown }).graphQLErrors)
  )
}

const shouldSuppressApolloGraphQLErrorTelemetry = (error: unknown): boolean => {
  if (!hasGraphQLErrors(error) || error.graphQLErrors.length === 0) {
    return false
  }

  return error.graphQLErrors.every((graphQLError) => {
    const extensions = (graphQLError.extensions ?? {}) as GraphQLErrorExtensions

    return extensions.reportToSentry === false
  })
}

const shouldBypassClientNetworkErrorIgnore = (error: unknown): boolean => {
  return (
    isErrorWithMessage(error) &&
    "name" in error &&
    isString(error.name) &&
    error.name === "GraphQLError"
  )
}

const MARKETING_CONSENT_SYNC_ENQUEUE_FAILED =
  "MARKETING_CONSENT_SYNC_ENQUEUE_FAILED"

function getErrorString(error: unknown): string {
  if (typeof error === "string") return error
  if (isErrorWithMessage(error)) return error.message
  return String(error)
}

export const getUserSaveErrorMessage = (error: unknown): string => {
  if (
    hasGraphQLErrors(error) &&
    error.graphQLErrors.some(
      (graphQLError) =>
        graphQLError.extensions?.code === MARKETING_CONSENT_SYNC_ENQUEUE_FAILED,
    )
  ) {
    return "Saving failed. Please try again."
  }

  return `Something went wrong: ${getErrorString(error)}`
}

export function toJSError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }

  const message = getErrorString(error)
  const jsError = new Error(message)

  if (isErrorWithName(error)) {
    jsError.name = error.name
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "stack" in error &&
    isString(error.stack)
  ) {
    jsError.stack = error.stack
  }

  if (typeof error === "object" && error !== null && "cause" in error) {
    jsError.cause = error.cause
  }

  return jsError
}

export const isErrorStatus = (status: number) => status >= 400

const IGNORED_CLIENT_NETWORK_ERROR_PATTERNS: RegExp[] = [
  /^(typeerror:\s*)?failed to fetch(?:\s+\([^)]+\))?$/i,
  /fetch is aborted/i,
  /networkerror when attempting to fetch resource/i,
  /(^|:\s)cancelled$/i,
  /aborterror/i,
  /^(timeouterror:\s*)?signal timed out$/i,
  /^network error$/i,
]

export const shouldIgnoreClientNetworkError = (error: unknown): boolean => {
  const candidates = [
    typeof error === "string" ? error : undefined,
    isErrorWithMessage(error) ? error.message : undefined,
    isErrorWithMessage(error) && "name" in error && isString(error.name)
      ? error.name
      : undefined,
  ].filter((value): value is string => typeof value === "string")

  return candidates.some((candidate) =>
    IGNORED_CLIENT_NETWORK_ERROR_PATTERNS.some((pattern) =>
      pattern.test(candidate),
    ),
  )
}

const setErrorScopeContext = (
  scope: SentryScope,
  extras?: Extras,
  isErrorBoundary?: boolean,
) => {
  if (extras) {
    scope.setExtras({ extras: JSON.stringify(extras, null, 2) })
  }
  if (isErrorBoundary) {
    scope.setTag("isErrorBoundary", true)
  }
  if (extras?.gqlCode) {
    const operationName = extras.operationName ?? "unknown"
    scope.setTag("gqlCode", extras.gqlCode)
    scope.setTag("operationName", operationName)
    scope.setFingerprint(["graphql", extras.gqlCode, operationName])
  }
}

const captureMessageToSentry = (message: string, extras?: Extras) => {
  if (!shouldReportToSentry(extras)) {
    return
  }

  Sentry.withScope((scope) => {
    setErrorScopeContext(scope, extras)
    Sentry.captureMessage(message)
  })
}

const captureExceptionToSentry = (
  error: unknown,
  extras?: Extras,
  isErrorBoundary?: boolean,
) => {
  if (!shouldReportToSentry(extras)) {
    return
  }

  Sentry.withScope((scope) => {
    setErrorScopeContext(scope, extras, isErrorBoundary)
    Sentry.captureException(toJSError(error))
  })
}

export const logErrorMessage = (message: string, extras?: Extras) => {
  if (shouldIgnoreClientNetworkError(message)) {
    return
  }

  track("appError", { ...extras, message })
  captureMessageToSentry(message, extras)
  if (!isProd()) {
    console.error(message)
  }
}

export const logError = (
  error: unknown,
  extras?: Extras,
  isErrorBoundary?: boolean,
) => {
  if (shouldSuppressApolloGraphQLErrorTelemetry(error)) {
    return
  }

  if (
    !shouldBypassClientNetworkErrorIgnore(error) &&
    shouldIgnoreClientNetworkError(error)
  ) {
    return
  }

  if (typeof error === "string") {
    logErrorMessage(error, extras)
    return
  }

  if (isErrorWithMessage(error)) {
    track("appError", {
      ...extras,
      message: error.message,
      isErrorBoundary,
    })
    captureExceptionToSentry(error, extras, isErrorBoundary)
  }

  if (!isProd()) {
    console.error(error, extras)
  }
}

export function getUserFriendlyErrorMessage(e: unknown): string {
  if (e === undefined) {
    return ""
  }

  if (isString(e)) {
    return e
  }

  let errors: GraphQLFormattedError[] = []

  if (isArray(e)) {
    errors = e
  }

  if (hasGraphQLErrors(e)) {
    errors = [...e.graphQLErrors]
  }

  if (errors.length > 0) {
    return errors.map(getUserFriendlyErrorMessage).join("\n")
  }

  if (isErrorWithMessage(e)) {
    return e.message
  }

  return "Unknown error"
}
